import { emptySplitApi as api } from "./emptyApi"

export type AssetMediaType = "IMAGE" | "VIDEO"

export type AssetSourceType =
  | "FACEBOOK_AD"
  | "TIKTOK_AD"
  | "SHOPIFY_PRODUCT"
  | "GEN_AI"
  | "UPLOAD"
  | "INSTAGRAM"
  | "TIKTOK"
  | "YOUTUBE_AD"
  | "YOUTUBE"

export interface AssetMapping {
  id: string
  assetId: string
  sourceType: AssetSourceType
  externalRefId: string
  sourceUrl?: string | null
  metadata?: Record<string, unknown> | null
}

export interface AssetCreative {
  source: "metadata" | "meta"
  primaryText: string
  headline: string
  description: string
  destinationUrl: string
  callToAction: string
  error?: string
}

export interface Asset {
  id: string
  companyId: string
  s3Key?: string | null
  thumbnailS3Key?: string | null
  width?: number | null
  height?: number | null
  status: "ACTIVE" | "ARCHIVED" | "MISSING" | "FAILED"
  syncStatus: "SYNCED" | "PENDING_DOWNLOAD" | "FAILED"
  mediaType: AssetMediaType
  metadata?: Record<string, unknown> | null
  perceptualHash?: string | null
  dominantColors?: unknown[] | null
  embedding?: number[] | null
  parentAssetId?: string | null
  createdAt: string
  updatedAt: string
  url?: string
  thumbnailUrl?: string
  mappings?: AssetMapping[]
  /** How many live AI children this asset has spawned (enhance / extend /
   *  motion / compose). Present only when non-zero — marks an original that
   *  already has variants so it doesn't look untouched next to them. */
  variantCount?: number
}

export interface ListAssetsRequest {
  limit?: number
  offset?: number
  status?: "ACTIVE" | "ARCHIVED" | "MISSING" | "FAILED"
  syncStatus?: "SYNCED" | "PENDING_DOWNLOAD" | "FAILED"
  sourceType?: AssetSourceType
  mediaType?: AssetMediaType
  /** Matches the upload file name, the source URL / external id, the asset id or the S3 key. */
  search?: string
  /** Only assets tagged to this feed ref (product id or `<productId>_<color>`). */
  productRefId?: string
  /** Only assets tagged anywhere on this product: its own ref or any color ref. */
  productFamilyRefId?: string
}

export interface ListAssetsResponse {
  assets: Asset[]
  total: number
}

export interface ResolveAssetsRequest {
  assets: Array<{
    sourceType: AssetSourceType
    externalRefId: string
    url?: string
    refType?: string
  }>
}

export interface AssetIdsByUrlResponse {
  /** url → assetId, containing only the URLs that matched a library asset. */
  ids: Record<string, string>
}

export interface ResolveAssetsResponse {
  results: Record<string, Asset | null>
  errors: Record<string, string>
}

export interface PresignedUploadRequest {
  fileName: string
  fileType: string
  thumbnailType?: string
  sourceType?: AssetSourceType
  parentAssetId?: string
  metadata?: string
  /** Feed `item_group_id` — tags the new asset into that product's pool. */
  productRefId?: string
}

/**
 * A product-feed tag on an asset: the asset belongs to the feed pool of the
 * product identified by `productRefId` (= feed `item_group_id` = Shopify
 * product id). Only MANUAL tags are user-removable; AI/SHOPIFY tags are
 * system-derived.
 */
export interface AssetProductTag {
  productRefId: string
  source: "MANUAL" | "AI" | "SHOPIFY"
  productName?: string
  productImageUrl?: string
}

export interface BulkProductTagsResponse {
  /** assetId → its product tags. Assets with no tags may be omitted. */
  results: Record<string, AssetProductTag[]>
}

export interface PresignedUploadResponse {
  fileUploadUrl: string
  thumbnailUploadUrl?: string
  assetId: string
  s3Key: string
}

export interface ConfirmUploadRequest {
  assetId: string
  /** Measured client-side — the bytes went straight to S3, so the server has
   *  no way to probe them. Without these the asset keeps a null resolution. */
  width?: number
  height?: number
}

export interface AssetsByPageUrlRequest {
  url: string
}

export interface AssetsByPageUrlResponse {
  snapshot: { id: string; entityId: string | null; pageType: string } | null
  assets: Asset[]
}

export interface PageTextBlock {
  /** heading | paragraph | list_item | spec | table | other */
  type: string
  text: string
}

export interface PageTextByPageUrlResponse {
  snapshot: { id: string; entityId: string | null; pageType: string } | null
  title: string | null
  metaDescription: string | null
  product: { name: string | null; description: string | null } | null
  blocks: PageTextBlock[]
}

const injectedRtkApi = api.injectEndpoints({
  endpoints: build => ({
    listAssets: build.query<ListAssetsResponse, ListAssetsRequest | void>({
      query: params => {
        const requestParams = params || {}
        return {
          url: `/assets`,
          method: "GET",
          params: {
            ...(requestParams.limit && {
              limit: requestParams.limit.toString(),
            }),
            ...(requestParams.offset && {
              offset: requestParams.offset.toString(),
            }),
            ...(requestParams.status && { status: requestParams.status }),
            ...(requestParams.syncStatus && {
              syncStatus: requestParams.syncStatus,
            }),
            ...(requestParams.sourceType && {
              sourceType: requestParams.sourceType,
            }),
            ...(requestParams.mediaType && {
              mediaType: requestParams.mediaType,
            }),
            ...(requestParams.search && {
              search: requestParams.search,
            }),
            ...(requestParams.productRefId && {
              productRefId: requestParams.productRefId,
            }),
            ...(requestParams.productFamilyRefId && {
              productFamilyRefId: requestParams.productFamilyRefId,
            }),
          },
        }
      },
      providesTags: ["Assets"],
    }),
    getAsset: build.query<Asset, string>({
      query: id => ({
        url: `/assets/${id}`,
        method: "GET",
      }),
      providesTags: (_result, _error, id) => [{ type: "Assets", id }],
    }),
    getAssetCreative: build.query<AssetCreative, string>({
      query: id => ({
        url: `/assets/${id}/creative`,
        method: "GET",
      }),
    }),
    uploadAsset: build.mutation<
      Asset,
      {
        file: File
        thumbnail?: File | null
        sourceType?: AssetSourceType
        parentAssetId?: string
        metadata?: Record<string, unknown>
      }
    >({
      query: ({ file, thumbnail, sourceType, parentAssetId, metadata }) => {
        const formData = new FormData()
        formData.append("file", file)
        if (thumbnail) {
          formData.append("thumbnail", thumbnail)
        }
        const searchParams = new URLSearchParams()
        if (sourceType) searchParams.set("sourceType", sourceType)
        if (parentAssetId) searchParams.set("parentAssetId", parentAssetId)
        if (metadata) searchParams.set("metadata", JSON.stringify(metadata))
        const qs = searchParams.toString()
        return {
          url: `/assets/upload${qs ? `?${qs}` : ""}`,
          method: "POST",
          body: formData,
        }
      },
      invalidatesTags: ["Assets"],
    }),
    // Pure lookup — no creation, no re-download. For URLs that already point at
    // the platform's asset bucket, this is the correct call; `resolveAssets`
    // would register a duplicate asset and re-fetch bytes we already store.
    assetIdsByUrl: build.query<AssetIdsByUrlResponse, { urls: string[] }>({
      query: ({ urls }) => ({
        url: `/assets/ids-by-url`,
        method: "GET",
        params: { urls },
      }),
      providesTags: ["Assets"],
    }),
    resolveAssets: build.mutation<ResolveAssetsResponse, ResolveAssetsRequest>({
      query: body => ({
        url: `/assets/resolve`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Assets"],
    }),
    presignedUpload: build.mutation<
      PresignedUploadResponse,
      PresignedUploadRequest
    >({
      query: body => ({
        url: `/assets/presigned-upload`,
        method: "POST",
        body,
      }),
    }),
    confirmUpload: build.mutation<Asset, ConfirmUploadRequest>({
      query: body => ({
        url: `/assets/confirm-upload`,
        method: "POST",
        body,
      }),
      // AssetProductTags too: a presigned upload carrying a productRefId
      // lands in the product's feed pool on confirm.
      invalidatesTags: ["Assets", "AssetProductTags"],
    }),
    /**
     * Replace an asset's thumbnail with a user-picked image (base64 JPEG) —
     * e.g. a frame chosen from the video itself in the AdForge thumbnail
     * picker. The chosen frame becomes the thumbnail Meta receives for
     * video (carousel) cards.
     */
    // Without `imageBase64` the server captures the frame itself from the S3
    // video at `frameTimeSec` (ffmpeg) — the media bucket blocks CORS reads,
    // so browser-side canvas capture is not possible for library videos.
    setAssetThumbnail: build.mutation<
      Asset,
      { assetId: string; imageBase64?: string; frameTimeSec?: number }
    >({
      query: ({ assetId, ...body }) => ({
        url: `/assets/${assetId}/thumbnail`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { assetId }) => [
        "Assets",
        { type: "Assets", id: assetId },
      ],
    }),
    getAssetsByPageUrl: build.query<
      AssetsByPageUrlResponse,
      AssetsByPageUrlRequest
    >({
      query: ({ url }) => ({
        url: `/assets/by-page-url`,
        method: "GET",
        params: { url },
      }),
    }),
    /**
     * Latest DB page-text extraction for a snapshotted page URL (title,
     * meta description, structured product copy, visible text blocks).
     * Empty content when the page was never crawled — callers fall back to
     * a live scrape.
     */
    getPageTextByPageUrl: build.query<
      PageTextByPageUrlResponse,
      AssetsByPageUrlRequest
    >({
      query: ({ url }) => ({
        url: `/assets/page-text-by-page-url`,
        method: "GET",
        params: { url },
      }),
    }),
    /**
     * Batch-fetch child variants for a list of parent asset ids. Returns
     * `Record<parentId, Asset[]>` where each `Asset` carries
     * `metadata.aspectRatio` (e.g. "9:16") set by the AI generation flow.
     * AdForge uses this to pre-fill placement slots with existing variants
     * so the user doesn't regenerate the same ratio.
     */
    getVariantsBulk: build.mutation<
      { results: Record<string, Asset[]> },
      { parentAssetIds: string[] }
    >({
      query: body => ({
        url: `/assets/variants/bulk`,
        method: "POST",
        body,
      }),
    }),
    /**
     * Batch-fetch product-feed tags for a list of asset ids (≤200). POST so
     * the id list doesn't blow the URL length cap; still a query so results
     * cache + refetch via the AssetProductTags tag.
     */
    bulkProductTags: build.query<
      BulkProductTagsResponse,
      { assetIds: string[] }
    >({
      query: body => ({
        url: `/assets/product-tags/bulk`,
        method: "POST",
        body,
      }),
      providesTags: ["AssetProductTags"],
    }),
    /** Tag an asset into a feed product's pool (source = MANUAL). */
    tagAssetProduct: build.mutation<
      AssetProductTag,
      { assetId: string; productRefId: string }
    >({
      query: ({ assetId, productRefId }) => ({
        url: `/assets/${assetId}/product-tags`,
        method: "POST",
        body: { productRefId },
      }),
      invalidatesTags: ["AssetProductTags"],
    }),
    /** Remove a MANUAL product tag from an asset. */
    untagAssetProduct: build.mutation<
      { deleted: number },
      { assetId: string; productRefId: string }
    >({
      query: ({ assetId, productRefId }) => ({
        url: `/assets/${assetId}/product-tags/${encodeURIComponent(productRefId)}`,
        method: "DELETE",
      }),
      invalidatesTags: ["AssetProductTags"],
    }),
    /**
     * Resolve any asset (typically an AI-generated child) into its AdForge
     * "card": the ORIGINAL source image up the lineage + the per-placement
     * extend variants found across that lineage, keyed by slot. Lets the editor
     * reassemble a whole card from a single selected variant.
     */
    getResolvedCard: build.query<ResolvedCard, string>({
      query: id => `/assets/${id}/card`,
    }),
  }),
  overrideExisting: false,
})

export type ResolvedCardSlot = "landscape" | "portrait" | "stories" | "square"
export interface ResolvedCard {
  main: Asset | null
  placements: Partial<
    Record<ResolvedCardSlot, { assetId: string; url?: string }>
  >
}

export const assetsApi = injectedRtkApi

export const {
  useListAssetsQuery,
  useGetAssetQuery,
  useGetAssetCreativeQuery,
  useLazyGetAssetCreativeQuery,
  useUploadAssetMutation,
  useAssetIdsByUrlQuery,
  useLazyAssetIdsByUrlQuery,
  useResolveAssetsMutation,
  usePresignedUploadMutation,
  useConfirmUploadMutation,
  useSetAssetThumbnailMutation,
  useGetAssetsByPageUrlQuery,
  useLazyGetAssetsByPageUrlQuery,
  useGetPageTextByPageUrlQuery,
  useLazyGetPageTextByPageUrlQuery,
  useGetVariantsBulkMutation,
  useLazyGetResolvedCardQuery,
  useBulkProductTagsQuery,
  useTagAssetProductMutation,
  useUntagAssetProductMutation,
} = injectedRtkApi
